/**
 * Frontend performance collector.
 *
 * Snapshots Web Vitals, navigation timing, and the heaviest network
 * resources from the browser's PerformanceObserver / Resource Timing
 * API. Pure read-only — no telemetry is sent anywhere; the admin
 * Performance page reads these numbers directly from the same browser
 * session.
 */

export interface WebVitals {
  /** First Contentful Paint, ms since navigation. */
  fcp: number | null;
  /** Largest Contentful Paint, ms since navigation. */
  lcp: number | null;
  /** Cumulative Layout Shift, unitless. */
  cls: number | null;
  /** Interaction to Next Paint (or first INP candidate), ms. */
  inp: number | null;
  /** Time to First Byte, ms since navigation start. */
  ttfb: number | null;
}

export interface NavTiming {
  /** Total time from navigation start to load event (ms). */
  loadTime: number | null;
  /** DOMContentLoaded relative to navigation start (ms). */
  domContentLoaded: number | null;
  /** DNS lookup duration (ms). */
  dns: number | null;
  /** TCP connect duration (ms). */
  tcp: number | null;
  /** Server response duration (ms). */
  responseTime: number | null;
  /** DOM processing duration (ms). */
  domProcessing: number | null;
}

export interface ResourceEntry {
  name: string;
  shortName: string;
  initiatorType: string;
  duration: number;
  transferSize: number;
  startTime: number;
}

export interface FrontendPerfSnapshot {
  capturedAt: string;
  vitals: WebVitals;
  nav: NavTiming;
  resources: ResourceEntry[];
  longTasks: number;
  longTaskTotalMs: number;
}

const safe = (n: number | undefined | null): number | null =>
  typeof n === "number" && isFinite(n) ? Math.round(n * 100) / 100 : null;

const getNavTiming = (): NavTiming => {
  const [nav] = performance.getEntriesByType(
    "navigation",
  ) as PerformanceNavigationTiming[];
  if (!nav) {
    return {
      loadTime: null,
      domContentLoaded: null,
      dns: null,
      tcp: null,
      responseTime: null,
      domProcessing: null,
    };
  }
  return {
    loadTime: safe(nav.loadEventEnd - nav.startTime),
    domContentLoaded: safe(nav.domContentLoadedEventEnd - nav.startTime),
    dns: safe(nav.domainLookupEnd - nav.domainLookupStart),
    tcp: safe(nav.connectEnd - nav.connectStart),
    responseTime: safe(nav.responseEnd - nav.requestStart),
    domProcessing: safe(nav.domComplete - nav.responseEnd),
  };
};

const getPaint = (): { fcp: number | null; ttfb: number | null } => {
  const fcp =
    performance.getEntriesByName("first-contentful-paint")[0]?.startTime ??
    null;
  const [nav] = performance.getEntriesByType(
    "navigation",
  ) as PerformanceNavigationTiming[];
  return {
    fcp: safe(fcp),
    ttfb: nav ? safe(nav.responseStart - nav.startTime) : null,
  };
};

// Live-updating vitals that need an observer (LCP, CLS, INP).
const live = {
  lcp: null as number | null,
  cls: 0 as number,
  inp: null as number | null,
  longTasks: 0,
  longTaskTotalMs: 0,
};

let observersInstalled = false;

const installObservers = () => {
  if (observersInstalled || typeof PerformanceObserver === "undefined") return;
  observersInstalled = true;

  // LCP: keep latest reported entry.
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1] as PerformanceEntry & {
        renderTime?: number;
        loadTime?: number;
      };
      if (last) {
        live.lcp = (last.renderTime || last.loadTime || last.startTime) ?? null;
      }
    }).observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    /* unsupported */
  }

  // CLS: accumulate session-window style.
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as Array<
        PerformanceEntry & { hadRecentInput?: boolean; value?: number }
      >) {
        if (!entry.hadRecentInput && typeof entry.value === "number") {
          live.cls += entry.value;
        }
      }
    }).observe({ type: "layout-shift", buffered: true });
  } catch {
    /* unsupported */
  }

  // INP / event timing: track worst interaction so far.
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as PerformanceEventTiming[]) {
        if (typeof entry.duration === "number") {
          if (live.inp === null || entry.duration > live.inp) {
            live.inp = entry.duration;
          }
        }
      }
    }).observe({ type: "event", buffered: true, durationThreshold: 16 } as PerformanceObserverInit);
  } catch {
    /* unsupported */
  }

  // Long tasks (≥50ms blocking the main thread).
  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        live.longTasks += 1;
        live.longTaskTotalMs += entry.duration;
      }
    }).observe({ type: "longtask", buffered: true });
  } catch {
    /* unsupported */
  }
};

const shortenName = (url: string): string => {
  try {
    const u = new URL(url, window.location.origin);
    const path = u.pathname.split("/").filter(Boolean);
    const tail = path.slice(-2).join("/") || u.hostname;
    return tail.length > 60 ? `…${tail.slice(-58)}` : tail;
  } catch {
    return url.length > 60 ? `…${url.slice(-58)}` : url;
  }
};

const getTopResources = (limit = 25): ResourceEntry[] => {
  const entries = performance.getEntriesByType(
    "resource",
  ) as PerformanceResourceTiming[];
  return entries
    .map((e) => ({
      name: e.name,
      shortName: shortenName(e.name),
      initiatorType: e.initiatorType || "other",
      duration: Math.round(e.duration * 100) / 100,
      transferSize: e.transferSize || 0,
      startTime: Math.round(e.startTime),
    }))
    .sort((a, b) => b.duration - a.duration)
    .slice(0, limit);
};

/** Take a snapshot of current frontend performance metrics. */
export const captureFrontendPerf = (): FrontendPerfSnapshot => {
  installObservers();
  const paint = getPaint();
  return {
    capturedAt: new Date().toISOString(),
    vitals: {
      fcp: paint.fcp,
      ttfb: paint.ttfb,
      lcp: safe(live.lcp),
      cls: safe(live.cls),
      inp: safe(live.inp),
    },
    nav: getNavTiming(),
    resources: getTopResources(),
    longTasks: live.longTasks,
    longTaskTotalMs: Math.round(live.longTaskTotalMs),
  };
};

/** Install observers eagerly so vitals start collecting at app boot. */
export const initFrontendPerfObservers = () => installObservers();
